/**
 * ComponentEditor — registry-based dynamic editor form per component type
 */
import type { ReactNode } from 'react';
import { EditorField } from '@/components/editor/EditorField';
import { EditorTextarea } from '@/components/editor/EditorTextarea';
import { EditorSelect } from '@/components/editor/EditorSelect';
import { EditorList } from '@/components/editor/EditorList';
import { EditorCheckbox } from '@/components/editor/EditorCheckbox';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComponentData {
  id: string;
  type: string;
  name: string;
  enabled: boolean;
  data: any;
}

interface ComponentEditorProps {
  component: ComponentData;
  settings: any;
  onUpdate: (data: any) => void;
}

// ---------------------------------------------------------------------------
// Editor Registry — each entry is a function (data, update) => JSX
// ---------------------------------------------------------------------------

type EditorFn = (data: any, update: (key: string, value: any) => void) => ReactNode;

const editorRegistry: Record<string, EditorFn> = {
  header: (data, update) => (
    <>
      <EditorField label="Logo Text" value={data.logoText} onChange={(v) => update('logoText', v)} placeholder="BF" />
      <EditorField label="Logo URL (optional)" value={data.logoUrl} onChange={(v) => update('logoUrl', v)} placeholder="https://..." />
      <EditorField label="Firmenname" value={data.companyName} onChange={(v) => update('companyName', v)} />
      <EditorField label="CTA Button" value={data.ctaText} onChange={(v) => update('ctaText', v)} />
      <EditorList
        label="Navigation"
        items={(data.navItems || []).map((n: string) => ({ text: n }))}
        onChange={(items) => update('navItems', items.map((i: any) => i.text))}
        fields={[{ key: 'text', placeholder: 'Link-Text' }]}
        maxItems={8}
      />
    </>
  ),

  hero: (data, update) => (
    <>
      <EditorField label="Headline" value={data.title} onChange={(v) => update('title', v)} />
      <EditorTextarea label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} rows={3} />
      <EditorField label="Prim\u00e4rer Button" value={data.primaryBtn} onChange={(v) => update('primaryBtn', v)} />
      <EditorField label="Sekund\u00e4rer Button" value={data.secondaryBtn} onChange={(v) => update('secondaryBtn', v)} />
      <EditorField label="Bild URL" value={data.imageUrl} onChange={(v) => update('imageUrl', v)} placeholder="https://..." />
    </>
  ),

  trust: (data, update) => (
    <EditorList
      label="Statistiken"
      items={data.items || []}
      onChange={(items) => update('items', items)}
      fields={[
        { key: 'value', placeholder: '500+', default: '0' },
        { key: 'label', placeholder: 'Kunden', default: 'Label' },
      ]}
      maxItems={5}
    />
  ),

  authority: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorSelect
        label="Variante"
        value={data.variant || 'chips'}
        onChange={(v) => update('variant', v)}
        options={[
          { value: 'chips', label: 'Chips' },
          { value: 'list', label: 'Liste' },
        ]}
      />
      <EditorList
        label="Eintr\u00e4ge"
        items={(data.items || []).map((i: any) => ({ icon: i.icon, text: i.text }))}
        onChange={(items) => update('items', items.map((it: any) => ({ icon: it.icon, text: it.text })))}
        fields={[
          { key: 'icon', placeholder: 'Icon (z.B. \u{1F3DB}\uFE0F)' },
          { key: 'text', placeholder: 'Text' },
        ]}
        maxItems={6}
      />
    </>
  ),

  proofTable: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorTextarea label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} rows={2} />
      <EditorList
        label="Header (3 Spalten)"
        items={(data.headers || []).map((h: string) => ({ text: h }))}
        onChange={(items) => update('headers', items.map((i: any) => i.text).slice(0, 3))}
        fields={[{ key: 'text', placeholder: 'Header' }]}
        maxItems={3}
      />
      <EditorSelect
        label="Highlight-Spalte"
        value={String(data.highlightColumn ?? 1)}
        onChange={(v) => update('highlightColumn', parseInt(v, 10))}
        options={[
          { value: '0', label: 'Kriterium' },
          { value: '1', label: 'Wir' },
          { value: '2', label: 'Andere' },
        ]}
      />
      <EditorList
        label="Zeilen"
        items={(data.rows || []).map((r: any) => ({ criterion: r.criterion, us: r.us, other: r.other }))}
        onChange={(items) => update('rows', items.map((it: any) => ({ criterion: it.criterion, us: it.us, other: it.other })))}
        fields={[
          { key: 'criterion', placeholder: 'Kriterium' },
          { key: 'us', placeholder: 'Wir' },
          { key: 'other', placeholder: 'Andere' },
        ]}
        maxItems={10}
      />
    </>
  ),

  process: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorTextarea label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} rows={2} />
      <EditorList
        label="Schritte"
        items={(data.steps || []).map((s: any) => ({ num: s.num, title: s.title, text: s.text }))}
        onChange={(items) => update('steps', items.map((it: any) => ({ num: it.num, title: it.title, text: it.text })))}
        fields={[
          { key: 'num', placeholder: '01' },
          { key: 'title', placeholder: 'Titel' },
          { key: 'text', placeholder: 'Beschreibung' },
        ]}
        maxItems={6}
      />
    </>
  ),

  services: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorField label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} />
      <EditorList
        label="Leistungen"
        items={data.items || []}
        onChange={(items) => update('items', items)}
        fields={[
          { key: 'icon', placeholder: '\u{1F4CA} Emoji', default: '\u{1F4CB}' },
          { key: 'title', placeholder: 'Titel', default: 'Neue Leistung' },
          { key: 'text', placeholder: 'Beschreibung', type: 'textarea', default: '' },
        ]}
        maxItems={8}
      />
    </>
  ),

  benefits: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorList
        label="Vorteile"
        items={data.items || []}
        onChange={(items) => update('items', items)}
        fields={[
          { key: 'title', placeholder: 'Titel', default: 'Vorteil' },
          { key: 'text', placeholder: 'Beschreibung', type: 'textarea', default: '' },
        ]}
        maxItems={6}
      />
    </>
  ),

  team: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorField label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} />
      <EditorList
        label="Team-Mitglieder"
        items={data.members || []}
        onChange={(items) => update('members', items)}
        fields={[
          { key: 'name', placeholder: 'Name', default: 'Max Muster' },
          { key: 'role', placeholder: 'Position', default: 'Mitarbeiter' },
          { key: 'image', placeholder: 'Bild URL', default: '' },
          { key: 'bio', placeholder: 'Kurzbeschreibung', type: 'textarea', default: '' },
        ]}
        maxItems={8}
      />
    </>
  ),

  gallery: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorField label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} />
      <EditorList
        label="Bilder"
        items={data.images || []}
        onChange={(items) => update('images', items)}
        fields={[
          { key: 'url', placeholder: 'Bild URL', default: '' },
          { key: 'caption', placeholder: 'Bildunterschrift', default: '' },
        ]}
        maxItems={12}
      />
    </>
  ),

  testimonials: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorList
        label="Kundenstimmen"
        items={data.items || []}
        onChange={(items) => update('items', items)}
        fields={[
          { key: 'quote', placeholder: 'Zitat', type: 'textarea', default: '' },
          { key: 'author', placeholder: 'Name', default: 'Kunde' },
          { key: 'role', placeholder: 'Position/Ort', default: '' },
        ]}
        maxItems={6}
      />
    </>
  ),

  faq: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorField label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} />
      <EditorList
        label="Fragen & Antworten"
        items={data.items || []}
        onChange={(items) => update('items', items)}
        fields={[
          { key: 'question', placeholder: 'Frage', default: 'Neue Frage?' },
          { key: 'answer', placeholder: 'Antwort', type: 'textarea', default: '' },
        ]}
        maxItems={10}
      />
    </>
  ),

  cta: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorField label="Untertitel" value={data.subtitle} onChange={(v) => update('subtitle', v)} />
      <EditorField label="Telefon" value={data.phone} onChange={(v) => update('phone', v)} placeholder="+41 31 XXX XX XX" />
      <EditorField label="E-Mail" value={data.email} onChange={(v) => update('email', v)} placeholder="info@example.ch" />
    </>
  ),

  cookie: (data, update) => (
    <>
      <EditorField label="Titel" value={data.title} onChange={(v) => update('title', v)} />
      <EditorTextarea label="Text" value={data.text} onChange={(v) => update('text', v)} />
      <EditorField label="Datenschutz-Link" value={data.policyLink} onChange={(v) => update('policyLink', v)} />

      <div className="editor-divider" />

      <EditorField label="Button: Alle akzeptieren" value={data.acceptAll} onChange={(v) => update('acceptAll', v)} />
      <EditorField label="Button: Ablehnen" value={data.acceptReject || ''} onChange={(v) => update('acceptReject', v)} />
      <EditorField label="Button: Einstellungen" value={data.openSettings || ''} onChange={(v) => update('openSettings', v)} />
      <EditorField label="Button: Speichern" value={data.savePreferences || ''} onChange={(v) => update('savePreferences', v)} />

      <div className="editor-divider" />

      <EditorField label="Label: Notwendig" value={data.necessaryLabel || ''} onChange={(v) => update('necessaryLabel', v)} />
      <EditorField label="Label: Statistik" value={data.statisticsLabel || ''} onChange={(v) => update('statisticsLabel', v)} />
      <EditorField label="Label: Marketing" value={data.marketingLabel || ''} onChange={(v) => update('marketingLabel', v)} />
      <EditorField label="Default: Statistik aktiv?" value={data.defaultStatistics ? 'ja' : 'nein'} onChange={(v) => update('defaultStatistics', String(v).toLowerCase() === 'ja')} placeholder="ja/nein" />
      <EditorField label="Default: Marketing aktiv?" value={data.defaultMarketing ? 'ja' : 'nein'} onChange={(v) => update('defaultMarketing', String(v).toLowerCase() === 'ja')} placeholder="ja/nein" />

      <div className="editor-divider" />

      <EditorField label="Preference Center Titel" value={data.preferencesTitle || ''} onChange={(v) => update('preferencesTitle', v)} />
      <EditorTextarea label="Preference Center Text" value={data.preferencesText || ''} onChange={(v) => update('preferencesText', v)} />
      <EditorField label="Footer-Link Text" value={data.manageLinkText || ''} onChange={(v) => update('manageLinkText', v)} />
    </>
  ),

  stickyCta: (data, update) => (
    <>
      <EditorField label="Text" value={data.text} onChange={(v) => update('text', v)} />
      <EditorField label="Button Text" value={data.buttonText} onChange={(v) => update('buttonText', v)} />
      <EditorField label="Telefon" value={data.phone} onChange={(v) => update('phone', v)} />
      <EditorField label="Hinweis (optional)" value={data.note} onChange={(v) => update('note', v)} />
    </>
  ),

  footer: (data, update) => (
    <>
      <EditorField label="Firmenname" value={data.companyName} onChange={(v) => update('companyName', v)} />
      <EditorTextarea label="Tagline" value={data.tagline} onChange={(v) => update('tagline', v)} rows={2} />
      <EditorField label="Adresse" value={data.address} onChange={(v) => update('address', v)} />
      <EditorField label="Telefon" value={data.phone} onChange={(v) => update('phone', v)} />
      <EditorField label="E-Mail" value={data.email} onChange={(v) => update('email', v)} />
      <div style={{ marginTop: 16 }}><span className="field-label">Social Media</span></div>
      <EditorField label="LinkedIn URL" value={data.linkedin} onChange={(v) => update('linkedin', v)} placeholder="https://linkedin.com/..." />
      <EditorField label="Facebook URL" value={data.facebook} onChange={(v) => update('facebook', v)} placeholder="https://facebook.com/..." />
      <EditorField label="Instagram URL" value={data.instagram} onChange={(v) => update('instagram', v)} placeholder="https://instagram.com/..." />
    </>
  ),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ComponentEditor({ component, settings: _settings, onUpdate }: ComponentEditorProps) {
  const update = (key: string, value: any) => {
    onUpdate({ ...component.data, [key]: value });
  };

  const editor = editorRegistry[component.type];
  if (!editor) {
    return (
      <p style={{ color: 'var(--builder-text-muted)' }}>
        Keine Einstellungen verf\u00fcgbar.
      </p>
    );
  }

  return <>{editor(component.data, update)}</>;
}
